// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Lock } from 'lucide-react';
import { PremiumLock, EducBox, InputRow, ResultCard, fmt, calcIRPEF } from './shared';

// ── PAC vs Fondo Pensione comparison ──
const calcFondoPensione = (cap, anni, tasso, contributoAnnuo) => {
  let saldo = cap;
  for (let i = 0; i < anni; i++) saldo = saldo * (1 + tasso) + contributoAnnuo;
  return saldo;
};

// ── IRPEF savings ──
const calcRisparmioFiscale = (reddito, contributo) => {
  const irpefSenza = calcIRPEF(reddito);
  const irpefCon = calcIRPEF(Math.max(0, reddito - Math.min(contributo, 5300)));
  return irpefSenza - irpefCon;
};

// ── TFR projection ──
const calcTFR = (stipendioAnnuoLordo, anni) => {
  const accantTFR = stipendioAnnuoLordo / 13.5;
  return accantTFR * anni * 1.015; // simplified TFR growth
};

const PensioneCalc = () => {
  // Main calculator inputs
  const [eta, setEta] = useState('35');
  const [etaPensione, setEtaPensione] = useState('67');
  const [capitale, setCapitale] = useState('10000');
  const [contributoMensile, setContributoMensile] = useState('200');
  const [tasso, setTasso] = useState('4');

  // Section 2 – IRPEF
  const [redditoIrpef, setRedditoIrpef] = useState('35000');
  const [contributoFondo, setContributoFondo] = useState('3000');

  // Section 3 – TFR
  const [stipendioAnnuo, setStipendioAnnuo] = useState('30000');
  const [tipoLavoro, setTipoLavoro] = useState('dipendente');
  const [openSection, setOpenSection] = useState(null);

  const anni = Math.max(1, (parseInt(etaPensione) || 67) - (parseInt(eta) || 35));
  const K = parseFloat(capitale) || 0;
  const C = (parseFloat(contributoMensile) || 0) * 12;
  const T = (parseFloat(tasso) || 0) / 100;

  // Main result
  const capitaleFondo = calcFondoPensione(K, anni, T, C);

  // Section 1: PAC ETF vs Fondo (PAC ETF no tax benefit but flexible)
  const tassoETF = T + 0.01; // slightly higher return but no deductibility
  const capitaleETF = calcFondoPensione(K, anni, tassoETF, C);
  const beneficioFiscaleFondo = calcRisparmioFiscale(parseFloat(redditoIrpef) || 35000, C);
  const capitaleFondoConFiscale = capitaleFondo + beneficioFiscaleFondo * anni;

  // Section 2: IRPEF savings
  const risparmioAnnuo = calcRisparmioFiscale(parseFloat(redditoIrpef) || 0, parseFloat(contributoFondo) || 0);
  const risparmioTotale = risparmioAnnuo * anni;
  const contributoMaxDeducibile = Math.min(parseFloat(contributoFondo) || 0, 5164.57);

  // Section 3: TFR
  const stipAnnuo = parseFloat(stipendioAnnuo) || 0;
  const tfrAzienda = calcTFR(stipAnnuo, anni);
  const tfrFondo = tfrAzienda * 1.045; // fondi negoziali typically get ~4.5% extra

  return (
    <div className="space-y-6">
      {/* ── MAIN CALCULATOR ── */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative" data-testid="pensione-calc">
        <PremiumLock />
        <EducBox text="La pensione complementare integra quella INPS (che sarà inferiore all'80% del tuo stipendio per le generazioni future). Prima di investire in fondi pensione, considera la deducibilità fiscale fino a €5.300/anno (Legge Bilancio 2026): è il migliore scudo fiscale disponibile in Italia. Il TFR conferito al fondo non concorre al limite dei €5.300." />

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <InputRow label="Età attuale" value={eta} onChange={setEta} suffix="anni" />
          <InputRow label="Età pensionamento" value={etaPensione} onChange={setEtaPensione} suffix="anni" />
          <InputRow label="Capitale già accumulato (€)" value={capitale} onChange={setCapitale} prefix="€" />
          <InputRow label="Contributo mensile (€)" value={contributoMensile} onChange={setContributoMensile} prefix="€" />
          <InputRow label="Rendimento atteso (%/anno)" value={tasso} onChange={setTasso} suffix="%" tooltip="Fondi azionari storicamente ~5-7%, bilanciati ~3-5%, garantiti ~1-2%" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <ResultCard label="Anni di contribuzione" value={`${anni} anni`} />
          <ResultCard label="Contributo annuo" value={`€${fmt(C)}`} />
          <ResultCard label="Montante al pensionamento" value={`€${fmt(capitaleFondo)}`} gold />
          <ResultCard label="Rendita mensile stimata" value={`€${fmt(capitaleFondo / (12 * 20))}`} green sub="Basata su 20 anni di pagamento" />
        </div>
      </motion.div>

      {/* ── SECTION 1: Fondo Pensione vs PAC ETF ── */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => setOpenSection(openSection === 1 ? null : 1)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
          data-testid="section-fondo-vs-pac"
        >
          <div>
            <p className="text-sm font-semibold text-[#E8E8E4] text-left">Fondo Pensione vs PAC ETF — confronto</p>
            <p className="text-xs text-[#888780] mt-0.5">Quale strumento è meglio per la tua pensione integrativa?</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#888780] transition-transform flex-shrink-0 ml-4 ${openSection === 1 ? 'rotate-180' : ''}`} />
        </button>

        {openSection === 1 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-2">
                <InputRow label="Reddito annuo lordo (€)" value={redditoIrpef} onChange={setRedditoIrpef} prefix="€" tooltip="Serve per calcolare il beneficio fiscale del fondo" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[450px]">
                  <thead>
                    <tr className="border-b border-white/[0.08]">
                      <th className="py-2 px-3 text-left text-[#888780] font-medium text-xs"></th>
                      <th className="py-2 px-3 text-right text-[#3D7A52] font-semibold text-xs">Fondo Pensione</th>
                      <th className="py-2 px-3 text-right text-[#5B8DB8] font-semibold text-xs">PAC ETF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Montante lordo', `€${fmt(capitaleFondo)}`, `€${fmt(capitaleETF)}`],
                      ['Beneficio fiscale totale (IRPEF)', `+€${fmt(beneficioFiscaleFondo * anni)}`, '€0'],
                      ['Liquidità', 'Limitata (solo casi gravi)', 'Totale — vendi quando vuoi'],
                      ['Aliquota tassazione uscita', '9–15% (in base agli anni)', '26% capital gain'],
                      ['Contributo datore (dipendenti)', 'Possibile (contratto)', 'No'],
                      ['Flessibilità investimento', 'Comparti pre-definiti', 'Totale libertà'],
                    ].map(([label, fondo, pac], i) => (
                      <tr key={i} className="border-b border-white/[0.04]">
                        <td className="py-2.5 px-3 text-[#888780] text-xs">{label}</td>
                        <td className="py-2.5 px-3 text-right text-[#E8E8E4] text-xs font-medium">{fondo}</td>
                        <td className="py-2.5 px-3 text-right text-[#E8E8E4] text-xs font-medium">{pac}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 rounded-xl text-xs text-[#888780]" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.25)' }}>
                <strong className="text-[#3D7A52]">Strategia ottimale:</strong> Versa nel fondo pensione almeno quanto ti permette di avere il contributo del datore + la soglia di deducibilità fiscale (€5.300/anno, Legge Bilancio 2026). Investi il resto in ETF per mantenere liquidità.
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* ── SECTION 2: Vantaggi fiscali IRPEF ── */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => setOpenSection(openSection === 2 ? null : 2)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
          data-testid="section-vantaggi-fiscali"
        >
          <div>
            <p className="text-sm font-semibold text-[#E8E8E4] text-left">Vantaggi fiscali — quanto risparmi di IRPEF?</p>
            <p className="text-xs text-[#888780] mt-0.5">Calcola il risparmio fiscale annuo con la deduzione fino a €5.300 (Legge Bilancio 2026)</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#888780] transition-transform flex-shrink-0 ml-4 ${openSection === 2 ? 'rotate-180' : ''}`} />
        </button>

        {openSection === 2 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <InputRow label="Reddito lordo annuo (€)" value={redditoIrpef} onChange={setRedditoIrpef} prefix="€" />
                <InputRow label="Contributo annuo al fondo (€)" value={contributoFondo} onChange={setContributoFondo} prefix="€"
                  tooltip="Massimo deducibile: €5.164,57/anno" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="irpef-savings-results">
                <ResultCard label="Contributo effettivo" value={`€${fmt(contributoMaxDeducibile)}`} sub={contributoFondo > 5164.57 ? 'Limitato al massimale' : 'Interamente deducibile'} />
                <ResultCard label="Risparmio IRPEF annuo" value={`€${fmt(risparmioAnnuo)}`} gold />
                <ResultCard label="Risparmio totale (${anni} anni)" value={`€${fmt(risparmioTotale)}`} green />
                <ResultCard label="Costo netto effettivo/anno" value={`€${fmt(Math.max(0, parseFloat(contributoFondo) - risparmioAnnuo))}`} sub="Dopo il risparmio fiscale" />
              </div>

              <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <p className="text-xs font-medium text-[#C9A84C] mb-2">Come funziona la deduzione</p>
                <p className="text-xs text-[#888780] leading-relaxed">I contributi al fondo pensione sono <strong className="text-[#E8E8E4]">deducibili dal reddito imponibile</strong> (non detraibili) fino a €5.164,57/anno. Con un reddito di €{fmt(parseFloat(redditoIrpef)||0)}, versare €{fmt(contributoMaxDeducibile)} riduce le tasse di <strong className="text-[#C9A84C]">€{fmt(risparmioAnnuo)}</strong>. Il costo reale è quindi solo €{fmt(Math.max(0, parseFloat(contributoFondo) - risparmioAnnuo))}/anno.</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* ── SECTION 3: TFR ── */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => setOpenSection(openSection === 3 ? null : 3)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
          data-testid="section-tfr"
        >
          <div>
            <p className="text-sm font-semibold text-[#E8E8E4] text-left">TFR in azienda vs TFR nel fondo pensione</p>
            <p className="text-xs text-[#888780] mt-0.5">Solo per dipendenti — confronto dell'accumulazione del trattamento di fine rapporto</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#888780] transition-transform flex-shrink-0 ml-4 ${openSection === 3 ? 'rotate-180' : ''}`} />
        </button>

        {openSection === 3 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <InputRow label="Stipendio annuo lordo (€)" value={stipendioAnnuo} onChange={setStipendioAnnuo} prefix="€" />
                <div>
                  <label className="text-[#888780] text-sm block mb-1">Tipo di lavoro</label>
                  <select value={tipoLavoro} onChange={e => setTipoLavoro(e.target.value)}
                    className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
                    <option value="dipendente">Dipendente privato</option>
                    <option value="dipendente_pubblico">Dipendente pubblico (PA)</option>
                  </select>
                </div>
              </div>

              {tipoLavoro === 'dipendente_pubblico' ? (
                <div className="p-4 rounded-xl text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-[#E8E8E4] font-medium mb-1">Dipendenti pubblici: TFR sempre in azienda (INPDAP)</p>
                  <p>Per i dipendenti della PA, il TFR rimane nella gestione INPDAP/INPS e non può essere trasferito al fondo pensione. Tuttavia i dipendenti PA possono aprire un fondo pensione aperto con contributi propri.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-xs text-[#888780] mb-1">TFR lasciato in azienda</p>
                      <p className="text-2xl font-bold text-[#E8E8E4]">€{fmt(tfrAzienda)}</p>
                      <p className="text-xs text-[#888780] mt-1">Rivalutazione 1,5% + 75% ISTAT (mediamente 2-3%)</p>
                    </div>
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.25)' }}>
                      <p className="text-xs text-[#888780] mb-1">TFR nel fondo pensione</p>
                      <p className="text-2xl font-bold text-[#3D7A52]">€{fmt(tfrFondo)}</p>
                      <p className="text-xs text-[#888780] mt-1">Con rendimento medio fondo negoziale ~4,5% storico</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl text-xs text-[#888780]" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
                    <strong className="text-[#C9A84C]">Differenza stimata: +€{fmt(tfrFondo - tfrAzienda)}</strong> a favore del fondo pensione. Attenzione: la tassazione del TFR in uscita varia. In azienda l'aliquota media si abbassa di più per trattamenti lunghi.
                  </div>
                </div>
              )}
            </div>
</motion.div>
        )}
      </div>
    </div>
  );
};

export default PensioneCalc;
